import React, { useEffect, useState, useMemo } from "react";
import { Box, Stack, CircularProgress, Popover, Checkbox } from "@mui/material";
import { useNavigate } from "react-router";
import Topbar from "../../../../../component/TopBar";
import { getOnLoginEmployeeInfo } from "../../../../../services/globalData";
import { useOrgEmployees } from "../../../../../services/useOrgData";
import { useNotify } from "../../../../../component/NotificationProvider";
import { getAllLeaveRequests } from "../../../../../services/leave";
import { getAllExpense } from "../../../../../services/expense";
import { getPayrollRuns } from "../../../../../services/payroll";
import { getLeaveCalendar } from "../../../../../services/leave";
import { MONTHS, money, periodLabel, RUN_STATUS_LABEL } from "../payroll-new/payrollUtils";
import { deadlinePill, PILL_DANGER } from "./waitingRules";
import { getAllDayAttendance } from "../../../../../services/facialRecognition";
import useTodayAttendance from "../../../../../services/useTodayAttendance";
import { currentDate } from "../../../../../utils/currentDate";
import { tokens, displayFont, motion, elevation } from "../../../../../theme";
import EmptyState from "../../../../../component/EmptyState";

import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";

import { pressable } from "../../../../../component/pressableProps";
import { counterRowSx } from "../../../container/frameLayout";
/**
 * A figure at the foot of the page.
 *
 * The book puts these last, not first: "Numbers nobody can act on are the last
 * thing on it, not the first." They lost their icon chips and their arrow in
 * the move — a tile that looks like a button invites a click that only ever
 * repeats what the tile already said.
 */
const StatTile = ({ label, value, sub, loading, onClick }) => (
  <Box
    {...pressable(onClick)}
    sx={{
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      padding: "18px",
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { borderColor: tokens.borderStrong } : {},
    }}
  >
    <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted, mb: "10px" }}>
      {label}
    </Box>
    <Box sx={{ fontFamily: displayFont, fontSize: "1.75rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: tokens.text, lineHeight: 1.2 }}>
      {loading ? <CircularProgress size={20} sx={{ color: tokens.textMuted }} /> : value}
    </Box>
    {sub && <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "4px" }}>{sub}</Box>}
  </Box>
);

/**
 * A quick action.
 *
 * The book: "Quick actions are five at most, all outlined, all creating
 * something... which is why none of them is the primary colour: the things that
 * cannot wait are in the list underneath." So the tinted icon chip is gone and
 * the icon takes the row's own colour.
 */
const QuickAction = ({ icon, label, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    {...pressable(onClick, label)}
    sx={{
      border: `1px solid ${tokens.borderStrong}`,
      borderRadius: "10px",
      padding: "16px 18px",
      cursor: "pointer",
      color: tokens.textMuted,
      minWidth: 0,
      transition: `border-color 120ms ${motion.easing.standard}, background 120ms ${motion.easing.standard}`,
      "&:hover": { borderColor: tokens.primary, background: tokens.surfaceSubtle },
    }}
  >
    <Box sx={{ display: "flex", flexShrink: 0, "& svg": { fontSize: "1.25rem" } }}>{icon}</Box>
    <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, minWidth: 0 }}>{label}</Box>
  </Stack>
);

/**
 * One thing waiting on the reader.
 *
 * The book is firm that this block "is always a list of actions, never a set of
 * counters" — so every row ends in the control that deals with it, and the row
 * says what happens if it is left alone.
 */
const WaitingRow = ({ title, detail, pill, pillTone, actionLabel, primary, onAction, last }) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ gap: "16px", padding: "14px 0", borderBottom: last ? "none" : `1px solid ${tokens.border}`, flexWrap: "wrap", rowGap: "0.5rem" }}
  >
    <Box sx={{ flex: 1, minWidth: "12rem" }}>
      <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mb: "2px" }}>{title}</Box>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{detail}</Box>
    </Box>
    {pill && (
      <Box
        component="span"
        sx={{
          background: pillTone?.bg,
          color: pillTone?.fg,
          borderRadius: "999px",
          padding: "4px 11px",
          fontSize: "0.75rem",
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {pill}
      </Box>
    )}
    <Box
      component="button"
      type="button"
      onClick={onAction}
      sx={{
        flexShrink: 0,
        borderRadius: "8px",
        padding: "9px 16px",
        fontSize: "0.8125rem",
        fontWeight: 600,
        fontFamily: "inherit",
        cursor: "pointer",
        background: primary ? tokens.primary : "transparent",
        color: primary ? tokens.card : tokens.primary,
        border: primary ? "1px solid transparent" : `1px solid ${tokens.borderStrong}`,
        "&:hover": { background: primary ? tokens.primaryHover : tokens.surfaceSubtle },
      }}
    >
      {actionLabel}
    </Box>
  </Stack>
);

/**
 * The payroll card: the only deep plum panel on the screen.
 *
 * The book gives it that ground for one reason — "which is how the eye finds
 * the thing with a deadline" — so nothing else on the dashboard may take it,
 * and its action is the page's one marigold control.
 */
const PayrollCard = ({ run, loading, onGo }) => (
  <Box
    className="pl-on-dark"
    sx={{
      width: { xs: "100%", lg: "20rem" },
      flex: "none",
      background: tokens.primaryHover,
      borderRadius: "12px",
      padding: "24px",
      color: tokens.darkText,
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.mutedDark, mb: "16px" }}>
      {run ? `${periodLabel(run.month, run.year)} payroll` : "Payroll"}
    </Box>

    {loading ? (
      <Box className="pl-skeleton" sx={{ height: "2.5rem", borderRadius: "8px", opacity: 0.35 }} />
    ) : !run ? (
      <Box sx={{ fontSize: "0.875rem", color: tokens.mutedDark, lineHeight: 1.6 }}>
        No payroll run yet. The first one you create will show its total here.
      </Box>
    ) : (
      <>
        <Box sx={{ fontFamily: displayFont, fontSize: "2.125rem", fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
          {money(run.totals?.totalNet)}
        </Box>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.mutedDark, mt: "6px", mb: "20px" }}>
          Net, {run.totals?.employeeCount || 0} people
        </Box>
        <Stack sx={{ gap: "9px", fontSize: "0.8125rem", mb: "20px" }}>
          <Stack direction="row" justifyContent="space-between">
            <Box sx={{ color: tokens.mutedDark }}>Status</Box>
            <Box>{RUN_STATUS_LABEL[run.status] || run.status}</Box>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Box sx={{ color: tokens.mutedDark }}>Gross</Box>
            <Box sx={{ fontVariantNumeric: "tabular-nums" }}>{money(run.totals?.totalGross)}</Box>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Box sx={{ color: tokens.mutedDark }}>{run.paymentDate ? "Paid" : "Pay date"}</Box>
            <Box>
              {/* `paymentDate` only exists once the run has actually paid out;
                  before that the intended date is the one worth showing. */}
              {(() => {
                const d = run.paymentDate || run.scheduledPayDate;
                return d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "Not set";
              })()}
            </Box>
          </Stack>
        </Stack>
      </>
    )}

    <Box sx={{ flex: 1 }} />
    <Box
      component="button"
      type="button"
      onClick={onGo}
      sx={{
        background: tokens.marigold,
        color: tokens.onMarigold,
        border: "none",
        borderRadius: "8px",
        padding: "12px 18px",
        fontSize: "0.875rem",
        fontWeight: 600,
        fontFamily: "inherit",
        cursor: "pointer",
        width: "100%",
        "&:hover": { background: tokens.marigoldHover },
      }}
    >
      Go to payroll
    </Box>
  </Box>
);

/**
 * Payroll cost over the last six runs.
 *
 * "Trend charts sit at the bottom, in a single muted plum, and the current
 * month is the only bar at full strength." One series, so no legend and no
 * categorical palette — this is not a comparison, it is a shape.
 *
 * Currency is abbreviated here and nowhere else: the book allows it in charts
 * and forbids it in tables, because a figure someone might quote has to be
 * written out in full.
 */
const abbreviated = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${Math.round(v / 1000)}k`;
  return String(Math.round(v));
};

const TrendChart = ({ runs, loading }) => {
  const last = runs.slice(-6);
  const peak = Math.max(...last.map((r) => Number(r.totals?.totalNet) || 0), 1);

  return (
    <Box sx={{ flex: 1.3, minWidth: 0, border: `1px solid ${tokens.border}`, borderRadius: "10px", padding: "22px" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: "22px" }}>
        <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted }}>
          Payroll cost, last six runs
        </Box>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Net</Box>
      </Stack>

      {loading ? (
        <Box className="pl-skeleton" sx={{ height: "8.125rem", borderRadius: "8px" }} />
      ) : last.length === 0 ? (
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.6 }}>
          Nothing to plot yet. Six months of runs will draw a shape here.
        </Box>
      ) : (
        <>
          <Stack direction="row" alignItems="flex-end" sx={{ gap: "18px", height: "8.125rem" }}>
            {last.map((r, i) => {
              const current = i === last.length - 1;
              const pct = Math.max(6, ((Number(r.totals?.totalNet) || 0) / peak) * 100);
              return (
                <Stack key={`${r.year}-${r.month}`} sx={{ flex: 1, height: "100%", justifyContent: "flex-end", alignItems: "center", gap: "8px" }}>
                  {current && (
                    <Box sx={{ fontSize: "0.6875rem", fontVariantNumeric: "tabular-nums", color: tokens.textMuted }}>
                      {abbreviated(r.totals?.totalNet)}
                    </Box>
                  )}
                  <Box
                    title={`${periodLabel(r.month, r.year)} — ${money(r.totals?.totalNet)}`}
                    sx={{
                      width: "100%",
                      height: `${pct}%`,
                      borderRadius: "4px 4px 0 0",
                      background: current ? tokens.primary : tokens.primaryTint,
                    }}
                  />
                </Stack>
              );
            })}
          </Stack>
          <Stack direction="row" sx={{ gap: "18px", mt: "10px", fontSize: "0.6875rem", color: tokens.textMuted }}>
            {last.map((r) => (
              <Box key={`${r.year}-${r.month}-label`} sx={{ flex: 1, textAlign: "center" }}>
                {(MONTHS[r.month] || "").slice(0, 3)}
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

/** Who is away today, and why. */
const AwayToday = ({ people, loading }) => (
  <Box sx={{ flex: 1, minWidth: 0, border: `1px solid ${tokens.border}`, borderRadius: "10px", padding: "22px" }}>
    <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted, mb: "18px" }}>
      Away today
    </Box>
    {loading ? (
      <Stack sx={{ gap: "12px" }}>
        {[0, 1, 2].map((i) => <Box key={i} className="pl-skeleton" sx={{ height: "1.875rem", borderRadius: "8px" }} />)}
      </Stack>
    ) : people.length === 0 ? (
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.6 }}>
        Everybody is in today.
      </Box>
    ) : (
      <Stack sx={{ gap: "12px" }}>
        {people.slice(0, 4).map((p) => (
          <Stack key={p.id} direction="row" alignItems="center" sx={{ gap: "11px" }}>
            <Box
              aria-hidden="true"
              sx={{
                width: "1.875rem", height: "1.875rem", borderRadius: "50%", flexShrink: 0,
                background: tokens.primaryTint, color: tokens.primary,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.6875rem", fontWeight: 700,
              }}
            >
              {p.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {p.name}
              </Box>
              <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>{p.reason}</Box>
            </Box>
          </Stack>
        ))}
        {people.length > 4 && (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
            {people.length - 4} more away today
          </Box>
        )}
      </Stack>
    )}
  </Box>
);

/**
 * Which blocks this person wants to see.
 *
 * Kept per browser rather than on the account: it is a view preference, not a
 * setting anyone else needs to know about, and a dashboard that has to wait for
 * a round trip before it knows what to draw flashes the wrong layout first.
 *
 * "Waiting on you" is deliberately absent from the list — the book makes it the
 * one block that is always first and always present, so it is not the reader's
 * to hide.
 */
const BLOCKS = [
  { key: "quickActions", label: "Quick actions" },
  { key: "payroll", label: "Payroll" },
  { key: "figures", label: "Figures" },
  { key: "trend", label: "Payroll trend" },
  { key: "away", label: "Away today" },
  { key: "attendance", label: "My attendance" },
];

const STORAGE_KEY = "pl-dashboard-blocks";


const readBlocks = () => {
  const all = BLOCKS.reduce((acc, b) => ({ ...acc, [b.key]: true }), {});
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    // Merge rather than replace, so a block added in a later release is on by
    // default instead of missing for everyone who has ever opened this menu.
    return { ...all, ...saved };
  } catch {
    return all;
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { data: employees } = useOrgEmployees();

  const [user, setUser] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [presentToday, setPresentToday] = useState(0);
  const [pendingLeave, setPendingLeave] = useState(0);
  const [pendingExpense, setPendingExpense] = useState(0);
  const [runs, setRuns] = useState([]);          // payroll card + trend
  const [awayToday, setAwayToday] = useState([]);
  const [sideLoading, setSideLoading] = useState(true);
  const [blocks, setBlocks] = useState(readBlocks);
  const [customiseAt, setCustomiseAt] = useState(null);

  const toggleBlock = (key) => {
    setBlocks((b) => {
      const next = { ...b, [key]: !b[key] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  };

  // personal check-in
  // Shared with the employee dashboard: re-read on focus, every minute and
  // after each punch, so a punch from the phone shows here too.
  const { inTime, isCheckedIn, punching: checkLoading, punch } = useTodayAttendance();

  useEffect(() => {
    setUser(getOnLoginEmployeeInfo()?.employee || null);
    fetchStats();
    fetchPanels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      // only today is rendered, so only today is fetched — this used to pull
      // every attendance record the org had ever produced to count one day
      const today = new Date().toISOString().split("T")[0];
      const [attendanceRes, leaveRes, expenseRes] = await Promise.all([
        getAllDayAttendance({ startDate: today, endDate: today }),
        getAllLeaveRequests(),
        getAllExpense(),
      ]);

      // still filtered client-side: the range is inclusive of whole days, and
      // this guards against a record carrying a time component
      const todays = (attendanceRes?.data || []).filter(
        (r) => new Date(r.date).toISOString().split("T")[0] === today
      );
      setPresentToday(new Set(todays.map((r) => r.employee)).size);

      const isPending = (s) => String(s || "").toLowerCase() === "pending";
      const leavePending = (leaveRes?.data || []).filter((x) => isPending(x.status));
      const expensePending = (expenseRes?.data || []).filter((x) => isPending(x.status));
      setPendingLeave(leavePending.length);
      setPendingExpense(expensePending.length);

    } catch (err) {
      notify.error(err?.message || "Couldn't load dashboard stats");
    } finally {
      setStatsLoading(false);
    }
  };

  /**
   * The payroll card, the trend and who is away.
   *
   * Fetched apart from the approval counts and settled with allSettled: a
   * dashboard where one dead endpoint blanks the whole page is worse than one
   * that renders four blocks and says nothing about the fifth.
   */
  const fetchPanels = async () => {
    setSideLoading(true);
    const today = new Date().toISOString().split("T")[0];
    const [runsRes, awayRes] = await Promise.allSettled([
      getPayrollRuns(),
      getLeaveCalendar({ from: today, to: today, status: "Approved" }),
    ]);

    if (runsRes.status === "fulfilled") {
      const list = (runsRes.value?.data || [])
        .filter((r) => r.runType !== "off_cycle")
        // newest last, so the trend reads left to right the way a calendar does
        .sort((a, b) => (a.year - b.year) || (a.month - b.month));
      setRuns(list);
    }

    if (awayRes.status === "fulfilled") {
      const people = (awayRes.value?.data || []).map((entry) => ({
        id: entry.employeeId || entry.id,
        name: entry.employee || "Someone",
        reason: entry.leaveType || "Leave",
      }));
      // one row per person, even when their leave spans several days
      const seen = new Set();
      setAwayToday(people.filter((x) => (seen.has(String(x.id)) ? false : seen.add(String(x.id)))));
    }

    setSideLoading(false);
  };

  const handleCheck = async () => {
    try {
      const done = await punch();
      notify.success(done === "IN" ? "Checked in" : "Checked out");
    } catch (err) {
      // the card has already re-read the server; say what it said
      notify.error(err?.message || "Couldn't update attendance");
    }
  };

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  }, []);


  /**
   * The "waiting on you" list, built from the counts we already hold.
   *
   * The book's block is aggregate — "6 payroll approvals", "4 leave requests" —
   * rather than a feed of individual records, because the question it answers is
   * "what is owed by me today", and four separate rows for four leave requests
   * answers that four times over.
   *
   * The most urgent row carries the solid button and the rest are outlined: one
   * primary per block, or the eye has nothing to land on.
   */
  const latestRun = runs.length ? runs[runs.length - 1] : null;
  const pendingRun = runs.find((r) => r.status === "pending_approval") || null;
  // A rejected run is the one state on this screen that stops people being
  // paid at all, so it takes the danger pill and sits above everything else.
  const rejectedRun = runs.find((r) => r.status === "rejected") || null;

  // The run itself knows who it could not pay: the count is snapshotted when the
  // run is built, so it is what was true then rather than what a since-edited
  // profile says now.
  const unpayable = latestRun?.totals?.missingBankDetails || 0;

  const waiting = [
    unpayable > 0 && {
      title: `${unpayable} ${unpayable === 1 ? "person has" : "people have"} no bank details`,
      detail: `They will be excluded from the ${latestRun ? periodLabel(latestRun.month, latestRun.year) : "current"} run`,
      pill: "Blocking",
      pillTone: PILL_DANGER,
      actionLabel: "Fix",
      onAction: () => navigate("/app/employees"),
    },
    rejectedRun && {
      title: `${periodLabel(rejectedRun.month, rejectedRun.year)} payroll was rejected`,
      detail: "Nobody on this run is paid until it is corrected and re-approved",
      pill: "Blocking",
      pillTone: PILL_DANGER,
      actionLabel: "Fix",
      onAction: () => navigate("/app/settings/payroll/runs"),
    },
    pendingRun && {
      title: `${periodLabel(pendingRun.month, pendingRun.year)} payroll approval`,
      detail: "The run cannot be submitted until this clears",
      ...(deadlinePill(pendingRun.scheduledPayDate || pendingRun.paymentDate) || {}),
      actionLabel: "Review",
      onAction: () => navigate("/app/settings/payroll/runs"),
    },
    pendingLeave > 0 && {
      title: `${pendingLeave} leave request${pendingLeave === 1 ? "" : "s"}`,
      detail: "Waiting on your approval",
      actionLabel: "Review",
      onAction: () => navigate("/app/leave-dashboard"),
    },
    pendingExpense > 0 && {
      title: `${pendingExpense} expense claim${pendingExpense === 1 ? "" : "s"}`,
      detail: "Waiting on your approval",
      actionLabel: "Open",
      onAction: () => navigate("/app/expense-requests"),
    },
  ].filter(Boolean);

  const headcount = employees?.length ?? 0;

  const tiles = [
    {
      label: "Headcount",
      value: headcount,
      sub: "People on the register",
      onClick: () => navigate("/app/employees"),
      always: true,
    },
    {
      label: "Clocked in today",
      value: presentToday,
      sub: headcount ? `of ${headcount} expected` : undefined,
      onClick: () => navigate("/app/attendance-dashboard"),
    },
    {
      label: "Leave requests",
      value: pendingLeave,
      sub: "Awaiting a decision",
      onClick: () => navigate("/app/leave-dashboard"),
    },
    {
      label: "Open claims",
      value: pendingExpense,
      sub: "Awaiting a decision",
      onClick: () => navigate("/app/expense-requests"),
    },
  ];

  const quickActions = [
    { icon: <GroupAddOutlinedIcon />, label: "Add a person", onClick: () => navigate("/app/employees/onboarding") },
    { icon: <EventBusyOutlinedIcon />, label: "Book leave", onClick: () => navigate("/app/leave-dashboard") },
    { icon: <ReceiptLongOutlinedIcon />, label: "New claim", onClick: () => navigate("/app/expense-dashboard") },
    { icon: <PaymentsOutlinedIcon />, label: "Go to payroll", onClick: () => navigate("/app/settings/payroll/dashboard") },
  ];


  return (
    <Box sx={{ width: "100%", paddingBlock: "1rem" }}>
      <Topbar />

      {/* Greeting. The subtitle carries the one number that frames the day, so
          the header answers "is today normal" before anything else is read. */}
      <Stack direction="row" alignItems="flex-start" sx={{ gap: "20px", mb: "28px" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.625rem", fontWeight: 700, color: tokens.text, mb: "4px", letterSpacing: "-0.01em" }}>
            {greeting}, {user?.fname || "there"}
          </Box>
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>
            {currentDate()}
            {headcount > 0 && ` · ${presentToday} of ${headcount} people clocked in`}
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={(e) => setCustomiseAt(e.currentTarget)}
          sx={{
            flexShrink: 0,
            background: "transparent",
            border: `1px solid ${tokens.borderStrong}`,
            color: tokens.primary,
            borderRadius: "8px",
            padding: "11px 18px",
            fontSize: "0.875rem",
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: "pointer",
            "&:hover": { background: tokens.surfaceSubtle },
          }}
        >
          Customise
        </Box>
      </Stack>

      <Popover
        open={Boolean(customiseAt)}
        anchorEl={customiseAt}
        onClose={() => setCustomiseAt(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { borderRadius: "10px", border: `1px solid ${tokens.border}`, boxShadow: elevation.raised, padding: "6px", minWidth: "13rem" } }}
      >
        <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted, padding: "10px 12px 6px" }}>
          Show on this dashboard
        </Box>
        {BLOCKS.map((b) => (
          <Box
            key={b.key}
            {...pressable(() => toggleBlock(b.key), b.label)}
            sx={{
              display: "flex", alignItems: "center", gap: "10px",
              padding: "9px 12px", borderRadius: "6px", fontSize: "0.8125rem",
              cursor: "pointer", color: tokens.text,
              "&:hover": { background: tokens.surface },
            }}
          >
            <Checkbox
              checked={Boolean(blocks[b.key])}
              size="small"
              tabIndex={-1}
              sx={{ padding: 0 }}
              inputProps={{ "aria-label": b.label }}
            />
            {b.label}
          </Box>
        ))}
      </Popover>

      {/* Quick actions: outlined, never the primary colour, because the things
          that cannot wait are in the list underneath. */}
      {blocks.quickActions && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: `repeat(${quickActions.length}, 1fr)` },
            gap: "12px",
            mb: "24px",
          }}
        >
          {quickActions.map((q) => (
            <QuickAction key={q.label} icon={q.icon} label={q.label} onClick={q.onClick} />
          ))}
        </Box>
      )}

      <Stack direction={{ xs: "column", lg: "row" }} sx={{ gap: "20px", mb: "24px", alignItems: "stretch" }}>
        {/* Always the first block on the page, and never hideable. */}
        <Box sx={{ flex: 1.2, minWidth: 0, border: `1px solid ${tokens.border}`, borderRadius: "12px", padding: "24px" }}>
          <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted, mb: "18px" }}>
            Waiting on you
          </Box>
          {statsLoading ? (
            <Stack sx={{ gap: "14px" }}>
              {[0, 1].map((i) => (
                <Box key={i} className="pl-skeleton" sx={{ height: "2.5rem", borderRadius: "8px" }} />
              ))}
            </Stack>
          ) : waiting.length === 0 ? (
            <EmptyState
              title="Nothing waiting on you"
              description="Every leave request and expense claim has been decided. New ones appear here as they are raised."
            />
          ) : (
            waiting.map((w, i) => (
              <WaitingRow key={w.title} {...w} primary={i === 0} last={i === waiting.length - 1} />
            ))
          )}
        </Box>

        {blocks.payroll && (
          <PayrollCard
            run={latestRun}
            loading={sideLoading}
            onGo={() => navigate("/app/settings/payroll/dashboard")}
          />
        )}
      </Stack>

      {/* Figures: nothing here is an action, so nothing here is first. */}
      {blocks.figures && (
        <Box
          sx={{
            ...counterRowSx(4),
            mb: "24px",
          }}
        >
          {tiles.map((t) => (
            <StatTile
              key={t.label}
              label={t.label}
              value={t.value}
              sub={t.sub}
              loading={t.always ? false : statsLoading}
              onClick={t.onClick}
            />
          ))}
        </Box>
      )}

      {(blocks.trend || blocks.away) && (
        <Stack direction={{ xs: "column", lg: "row" }} sx={{ gap: "20px", mb: "24px", alignItems: "stretch" }}>
          {blocks.trend && <TrendChart runs={runs} loading={sideLoading} />}
          {blocks.away && <AwayToday people={awayToday} loading={sideLoading} />}
        </Stack>
      )}

      {/* Personal clock-in. Not a block in the book's admin dashboard at all —
          it sits last so it cannot displace anything that is. */}
      {blocks.attendance && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          sx={{ gap: "20px", border: `1px solid ${tokens.border}`, borderRadius: "10px", padding: "18px 22px" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.textMuted, mb: "6px" }}>
              My attendance
            </Box>
            <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>
              {isCheckedIn && inTime
                ? `Clocked in at ${new Date(inTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : "You are not clocked in yet"}
            </Box>
            <Box
              {...pressable(() => navigate("/app/attendance-details/my-attendance"), "View my attendance")}
              sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.primary, cursor: "pointer", mt: "4px" }}
            >
              View my attendance
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            disabled={checkLoading}
            aria-label={isCheckedIn ? "Clock out" : "Clock in"}
            onClick={() => !checkLoading && handleCheck()}
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              borderRadius: "8px",
              padding: "11px 20px",
              fontFamily: "inherit",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: checkLoading ? "default" : "pointer",
              color: isCheckedIn ? tokens.primary : tokens.onPrimary,
              background: isCheckedIn ? "transparent" : tokens.primary,
              border: isCheckedIn ? `1px solid ${tokens.borderStrong}` : "1px solid transparent",
              transition: `background-color 120ms ${motion.easing.standard}`,
              "&:hover": checkLoading ? undefined : { background: isCheckedIn ? tokens.surfaceSubtle : tokens.primaryHover },
              "&:focus-visible": { outline: `2px solid ${tokens.primary}`, outlineOffset: "2px" },
            }}
          >
            {checkLoading ? (
              <CircularProgress size={18} sx={{ color: "inherit" }} />
            ) : (
              <>
                <TouchAppOutlinedIcon sx={{ fontSize: "1.125rem" }} />
                {isCheckedIn ? "Clock out" : "Clock in"}
              </>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default Dashboard;
